import { html, CSSResultGroup, TemplateResult } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import componentStyles from './tufts-carousel-yext-search.css.lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { ResizeController } from '../../controllers/resize-controller';
import Splide from '@splidejs/splide';
import {
  EntityDataStructure,
  RatingResponse,
  verticalSearchResponseStructure,
} from '../tufts-yext/tufts-yext-types';
import { ifDefined } from 'lit/directives/if-defined.js';
import {
  defaultImageTemplate,
  defaultImageTemplateSecond,
} from '../tufts-yext/image-template';
import {
  deleteDuplicateProfilesEntities,
  formatPhone,
} from '../tufts-yext/tufts-yext-format';
import '../tufts-card-personnel/tufts-card-personnel';
import { classMap } from 'lit/directives/class-map.js';
import { TuftsContainer } from '../tufts-container/tufts-container';
import '../tufts-icon/tufts-icon';

/**
 * The Tufts carousel yext search component
 *
 * @element tufts-carousel-yext-search
 */
@customElement('tufts-carousel-yext-search')
export class TuftsCarouselSearch extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  resizeController: ResizeController = new ResizeController(this);
  _splide: Splide;
  private _defaultTemplate: typeof defaultImageTemplate;
  isTintBackground = false;

  urlHref = 'https://cdn.yextapis.com/v2/accounts';
  accountId = 'me';
  contentType = 'entities';
  apiKey = 'f70a1166dcbde8731713f74b5d21b86f';
  urlBase = `${this.urlHref}/${this.accountId}/${this.contentType}`;
  apiVersion = '20230301';
  limitResultsYext = '50';
  // apiVersion = '20230406';

  @property({
    type: String,
    reflect: true,
    attribute: 'search',
  })
  search: string;
  @property({
    type: String,
    reflect: true,
    attribute: 'pairs',
  })
  pairs: string;

  @property({
    type: String,
    reflect: true,
    attribute: 'view-all-link',
  })
  viewAllLink: string;

  @property({
    type: Boolean,
    reflect: true,
    attribute: 'randomization',
  })
  randomization: false;

  @property({
    type: Boolean,
    reflect: true,
    attribute: 'not-render-carousel',
  })
  notRenderCarousel: false;

  @property({ type: Boolean, reflect: true, attribute: 'debug' })
  debug: null;

  @state()
  isMobile: boolean;

  @state()
  doctorResults: EntityDataStructure[] = [];

  @property({ type: Number, reflect: true, attribute: 'show-doctors' })
  showDoctors = 15;

  @property({ type: Number, reflect: true, attribute: 'specialty-number' })
  specialtyNumber = 5;

  @property({
    type: String,
    reflect: true,
    attribute: 'heading-text',
  })
  headingText = 'Meet our clinicians';

  @property({
    type: String,
    reflect: true,
    attribute: 'button-text',
  })
  buttonText = 'See all clinicians at this location';

  @query('#splide1')
  _splideRef: HTMLElement;
  addingclass = '';

  fetchDoctorFromYext() {
    const params = new URLSearchParams();

    params.set('api_key', this.apiKey);
    params.set('v', this.apiVersion);
    params.set('limit', this.limitResultsYext);
    params.set('randomization', this.randomization ? '1' : '0');

    if (this.search) {
      this.setRandomSpecialty(params);
    }

    params.set('entityTypes', 'healthcareProfessional');
    const url = `${this.urlBase}?${params.toString()}`;

    fetch(url)
      .then(response => response.json())
      .then(
        (doctorResponse: {
          meta: {};
          response: verticalSearchResponseStructure;
        }) => {
          // Check if there are results in the doctorResults array.
          if (doctorResponse.response.entities.length <= 0) {
            this.checkParentNodeAndUpdate(false);
            return;
          }
          deleteDuplicateProfilesEntities(doctorResponse.response);
          if (this.pairs) {
            this.handlePeerData(doctorResponse.response);
          }
          this.doctorResults = doctorResponse.response.entities.slice(
            0,
            this.showDoctors
          );

          // call rating after processing results
          this.fetchAdditionalProfileLocations(this.doctorResults);

          // Call checkParentNodeAndUpdate with hasResults=true
          this.checkParentNodeAndUpdate(true);
        }
      )
      .catch(error => {
        console.error(error);
        this.checkParentNodeAndUpdate(false);
      });
  }

  handlePeerData(response: verticalSearchResponseStructure) {
    // Parse the string representation of the "pairs" into an array of objects.
    const pairedEntities = JSON.parse(this.pairs) as {
      id: string;
      title: string;
    }[];

    response.entities.forEach(entity => {
      let matchingPairedEntity;
      if (entity.c_url_id) {
        // Find a matching object in "pairedEntities" based on the "id" property.
        matchingPairedEntity = pairedEntities.find(
          pairedEntity => pairedEntity.id === entity.c_url_id
        );
      }
      // Check if a match exists and ensure that "matchingPairedEntity.title" is not an empty string.
      if (matchingPairedEntity && matchingPairedEntity.title.length > 0) {
        // Create the "specialtyTitle" property of the entity with the "title" from the matched object.
        entity.specialtyTitle = [matchingPairedEntity.title];
      }
    });
  }

  setRandomSpecialty(params: URLSearchParams) {
    const specialties = this.getFilterValue(
      JSON.parse(this.search),
      'c_specialtyName'
    );
    if (specialties && specialties.length >= this.specialtyNumber) {
      const randomSpecialties: string[] = [];
      const chosenIndexes = new Set<number>();

      // ensure x unique random numbers
      while (chosenIndexes.size < this.specialtyNumber) {
        const randomIndex = Math.floor(Math.random() * specialties.length);
        chosenIndexes.add(randomIndex);
      }

      chosenIndexes.forEach((index: number) => {
        randomSpecialties.push(specialties[index]);
      });
      const newFilter = this.setFilterValue(
        JSON.parse(this.search),
        'c_specialtyName',
        randomSpecialties
      );

      const filters = JSON.stringify(newFilter);
      params.set('filter', filters);
    } else {
      const filters = JSON.parse(JSON.stringify(this.search));
      params.set('filter', filters);
    }
  }
  setFilterValue(
    filterObject: { $and: { string: string } },
    key: string,
    newValue: string[]
  ) {
    if (filterObject['$and'] instanceof Array) {
      const andArray = filterObject['$and'];
      for (const elementKey of andArray) {
        if (elementKey.hasOwnProperty(key)) {
          const existingKey = Object.keys(elementKey[key])[0];
          elementKey[key][existingKey] = newValue;
        }
      }
    }
    return filterObject;
  }

  /**
   * Checks the parent node for a background-color attribute
   * and updates it if there are no results
   *
   */

  checkParentNodeAndUpdate(hasResults = false) {
    // Look for the closest ancestor with the 'tufts-container' tag.
    const parentElement = this.closest('tufts-container') as Element;

    // If a 'tufts-container' ancestor is found
    if (parentElement) {
      if (!hasResults) {
        // If there are no results, set its 'background-color' attribute to 'white'.
        parentElement.setAttribute('background-color', 'white');
      } else {
        // If we have results, check if the container has a dark background color
        const bgColor = parentElement.getAttribute('background-color');
        const darkBackgrounds = [
          'green',
          'blue',
          'blue_dark',
          'purple',
          'purple_dark',
        ];

        if (bgColor && darkBackgrounds.includes(bgColor)) {
          // Add the dark-background class to the component for styling
          this.addingclass = 'dark-background';

          // When background is green, set pulse attributes
          if (bgColor === 'green') {
            // First set pulse-orientation to initialize the resizeController
            parentElement.setAttribute('pulse-orientation', 'bottom-left');

            // Give time for this update to be processed
            setTimeout(() => {
              // Then set the other attributes
              parentElement.setAttribute('max-pulse-size', '600px');
              parentElement.setAttribute('min-pulse-size', '25rem');
              parentElement.setAttribute('pulse-color', 'green');

              // Force an update of the container if possible
              if (parentElement instanceof HTMLElement) {
                const containerComp = parentElement as TuftsContainer;
                if (typeof containerComp.requestUpdate === 'function') {
                  containerComp.requestUpdate();
                }
              }
            }, 50); // Longer delay to ensure everything is processed correctly
          }
        }
      }
    }
  }

  firstUpdated() {
    if (this.notRenderCarousel) {
      this.checkParentNodeAndUpdate(false);
    } else {
      this.fetchDoctorFromYext();
    }
    // Determine which template to use once
    const parentContainer = this.closest('tufts-container');
    this.isTintBackground =
      parentContainer?.getAttribute('background-color') === 'tint';

    this._defaultTemplate = this.isTintBackground
      ? defaultImageTemplateSecond
      : defaultImageTemplate;
  }
  templateMobile() {
    return html`
      <div class="splide__track">
        <ul class="splide__list">
          ${this.doctorResults.map(doctor => {
            return html`
              <li class="splide__slide grid-one-result">
                ${this.tuftsCardPersonnelTemplate(doctor)}
              </li>
            `;
          })}
        </ul>
      </div>
      <div class="splide__arrows">
        <button
          class="splide__arrow splide__arrow--prev"
          aria-label="Previous"
        ></button>
        <button
          class="splide__arrow splide__arrow--next"
          aria-label="Next"
        ></button>
      </div>
    `;
  }
  divideArrayIntoGroups(array: EntityDataStructure[], groupSize = 3) {
    const dividedArray = [];
    for (let i = 0; i < array.length; i += groupSize) {
      const group = array.slice(i, i + groupSize);
      dividedArray.push(group);
    }
    return dividedArray;
  }

  templateDesktop(onlyOneResult: boolean) {
    const doctorResultsDivider = this.divideArrayIntoGroups(this.doctorResults);

    return html`
      <div class="splide__arrows">
        <button
          class="splide__arrow splide__arrow--prev"
          aria-label="Previous"
        ></button>
        <button
          class="splide__arrow splide__arrow--next"
          aria-label="Next"
        ></button>
      </div>
      <div class="splide__track">
        <ul class="splide__list">
          ${doctorResultsDivider.map(doc => {
            return html`
              <li class="splide__slide">
                <div
                  class="grid-desktop ${onlyOneResult
                    ? 'grid-one-result'
                    : null}"
                >
                  ${doc.map(
                    doctor => html`${this.tuftsCardPersonnelTemplate(doctor)}`
                  )}
                </div>
              </li>
            `;
          })}
        </ul>
      </div>
    `;
  }

  createReferenceSplide() {
    if (this._splide) {
      this._splide.destroy(true);
    }

    if (this._splideRef) {
      if (this.isMobile) {
        this._splide = new Splide(this._splideRef, {
          speed: 300,
          pagination: false,
        }).mount();
      } else {
        this._splide = new Splide(this._splideRef, {
          speed: 300,
        }).mount();
      }
    }
  }

  tuftsCardPersonnelTemplate(doctor: EntityDataStructure): TemplateResult {
    const profilePicture = doctor.headshot?.url;

    let profileDetailsUrl = '';

    if (doctor.c_url_id) {
      if (this.debug) {
        profileDetailsUrl = `/?path=/story/content-yext-yext-item--tufts-yext-item&yext=${doctor.id}`;
      } else {
        profileDetailsUrl = `/doctor/${doctor.c_url_id}`;
      }
    }
    const doctorSpecialty = doctor.c_specialtyName;
    return html`
      <tufts-card-personnel
        vertical-trigger-width="300"
        version="card-rating ${this.isTintBackground ? 'tint' : null}"
      >
        <div slot="photo">
          ${profilePicture
            ? html`<img
                alt="${ifDefined(doctor.name)}"
                src="${ifDefined(profilePicture)}"
              />`
            : this._defaultTemplate()}
        </div>
        <div slot="name">
          <tufts-heading
            text-color="heading-color"
            clickable-card
            level-size="md"
          >
            <h4>
              <a
                href="${profileDetailsUrl}"
                class="btn small link arrow-right"
                button-label="View Profile ${doctor.name}"
              >
                ${doctor.name}
              </a>
            </h4>
          </tufts-heading>
        </div>
        ${doctorSpecialty && doctorSpecialty.length !== 0
          ? html` <div slot="credentials">${doctorSpecialty.join(', ')}</div> `
          : null}
        ${doctor.rating
          ? html`<div slot="phone">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12.5 17.6015L8.37983 20.0836C8.19781 20.1994 8.00752 20.249 7.80896 20.2325C7.6104 20.2159 7.43666 20.1497 7.28774 20.0339C7.13881 19.9181 7.02299 19.7733 6.94025 19.5996C6.85752 19.4258 6.84097 19.2314 6.89061 19.0163L7.9827 14.3252L4.33412 11.1731C4.16865 11.0241 4.06523 10.8545 4.02387 10.6643C3.9825 10.474 3.99491 10.2878 4.0611 10.1058C4.12728 9.92378 4.22656 9.77486 4.35894 9.65903C4.49131 9.5432 4.67333 9.46874 4.90499 9.43565L9.72013 9.0137L11.5816 4.59569C11.6644 4.39712 11.7926 4.2482 11.9664 4.14892C12.1401 4.04964 12.318 4 12.5 4C12.682 4 12.8599 4.04964 13.0336 4.14892C13.2074 4.2482 13.3356 4.39712 13.4184 4.59569L15.2799 9.0137L20.095 9.43565C20.3267 9.46874 20.5087 9.5432 20.6411 9.65903C20.7734 9.77486 20.8727 9.92378 20.9389 10.1058C21.0051 10.2878 21.0175 10.474 20.9761 10.6643C20.9348 10.8545 20.8314 11.0241 20.6659 11.1731L17.0173 14.3252L18.1094 19.0163C18.159 19.2314 18.1425 19.4258 18.0597 19.5996C17.977 19.7733 17.8612 19.9181 17.7123 20.0339C17.5633 20.1497 17.3896 20.2159 17.191 20.2325C16.9925 20.249 16.8022 20.1994 16.6202 20.0836L12.5 17.6015Z"
                  fill="#00308C"
                />
              </svg>
              <span
                ><strong>${doctor.rating}</strong> (${doctor.comment_count}
                reviews)</span
              >
            </div>`
          : null}
        ${doctor.mainPhone
          ? html`<div slot="phone">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M18.9922 11.995C18.9922 10.0461 18.3126 8.397 16.9533 7.03776C15.5941 5.67851 13.945 4.99889 11.9961 4.99889V3C13.2454 3 14.4148 3.23987 15.5041 3.70961C16.5935 4.18934 17.543 4.82898 18.3526 5.63853C19.1621 6.44808 19.8018 7.39756 20.2815 8.48695C20.7512 9.57635 20.9911 10.7457 20.9911 11.995H18.9922Z"
                  fill="#428FEC"
                />
                <path
                  d="M14.9944 11.995C14.9944 11.1655 14.7046 10.4559 14.1149 9.8762C13.5252 9.29653 12.8256 8.99669 11.9961 8.99669V6.9978C13.3753 6.9978 14.5547 7.48753 15.5341 8.45699C16.5036 9.43645 16.9933 10.6058 16.9933 11.995H14.9944Z"
                  fill="#428FEC"
                />
                <path
                  d="M20.7701 15.2832C20.6202 15.1133 20.4303 14.9933 20.1905 14.9434L16.7424 14.2438C16.5125 14.2138 16.2726 14.2338 16.0328 14.3037C15.7929 14.3837 15.593 14.4936 15.4431 14.6435L13.0944 16.9922C11.8251 16.2227 10.6657 15.3232 9.60633 14.2737C8.54692 13.2243 7.66741 12.0949 6.9678 10.8956L9.39645 8.44697C9.54636 8.29706 9.64631 8.12715 9.68629 7.93726C9.72626 7.74736 9.73626 7.53748 9.69628 7.29761L9.04664 3.79956C9.01666 3.56968 8.90672 3.37979 8.71682 3.21988C8.53692 3.06996 8.32704 3 8.09717 3H4.04942C3.74958 3 3.49972 3.09994 3.29983 3.29983C3.09994 3.49972 3 3.74958 3 4.04942C3 6.19822 3.47973 8.29706 4.4392 10.3359C5.39867 12.3748 6.65797 14.1838 8.23709 15.7629C9.80622 17.342 11.6152 18.6013 13.6641 19.5608C15.7029 20.5203 17.8018 21 19.9506 21C20.2504 21 20.5003 20.9001 20.7002 20.7002C20.9001 20.5003 21 20.2504 21 19.9506V15.9028C21 15.673 20.92 15.4631 20.7801 15.2932L20.7701 15.2832ZM5.37868 7.02776C5.22876 6.35813 5.10883 5.68851 5.02887 4.99889H7.24764L7.66741 7.34758L6.01832 8.99667C5.73848 8.34703 5.5186 7.6874 5.36868 7.02776H5.37868ZM18.9911 18.9411C18.3115 18.8912 17.6319 18.7812 16.9522 18.6113C16.2726 18.4414 15.613 18.2215 14.9634 17.9417L16.6424 16.2726L18.9911 16.7424V18.9411Z"
                  fill="#00308C"
                />
              </svg>
              <span class="visually-hidden">Phone Number</span>
              <a href="tel:${doctor.mainPhone}"
                >${formatPhone(doctor.mainPhone)}</a
              >
            </div>`
          : null}
        ${doctor.c_openScheduling
          ? html`
              <div slot="view-profile">
                <a
                  class="btn small reversed compact open-scheduling"
                  href="/doctor/${doctor.c_url_id}?open-scheduling"
                  >Schedule online</a
                >
              </div>
            `
          : html`
              <div slot="view-profile">
                <a
                  class="btn small link arrow-right"
                  button-label="View Profile"
                >
                  View Profile
                </a>
              </div>
            `}
      </tufts-card-personnel>
    `;
  }

  // Function to generate search URL
  getFilterValue(objeto: { $and: { string: string } }, keyParams: string) {
    if (objeto['$and'] instanceof Array) {
      const andArray = objeto['$and'];
      for (const elementKey of andArray) {
        if (elementKey.hasOwnProperty(keyParams)) {
          const clave = Object.keys(elementKey[keyParams])[0];
          const value = elementKey[keyParams][clave];
          return value;
        }
      }
    }
    return null; // Value null if the key not exist
  }

  getSearchParameterFullAddress(searchParams: {
    $and: { string: string };
  }): string {
    const addressParts = [
      this.getFilterValue(searchParams, 'address.line1'),
      this.getFilterValue(searchParams, 'address.line2'),
      this.getFilterValue(searchParams, 'address.city'),
      this.getFilterValue(searchParams, 'address.region'),
      this.getFilterValue(searchParams, 'address.postalCode'),
    ].filter(Boolean);

    return addressParts.join(' ');
  }

  /**
   * Generates the view all button template with appropriate URL parameters
   * @returns {TemplateResult|null} The button template or null if viewAllLink is not set
   */
  viewAllButtonTemplate() {
    if (!this.viewAllLink) {
      return null;
    }
    const params = new URLSearchParams();
    params.set('y_input', this.viewAllLink);

    const profileDetailsUrl = this.debug
      ? `/iframe.html?id=content-yext-yext-list--tufts-yext&args=&viewMode=story&${params.toString()}`
      : `/find-a-doctor?${params.toString()}`;

    return html`
      <div class="container-button-doctors">
        <tufts-button
          button-variant="link"
          button-url=${profileDetailsUrl}
          button-label="${this.buttonText}"
        >
          ${this.buttonText}
        </tufts-button>
      </div>
    `;
  }

  async fetchAdditionalProfileLocations(searchResults: EntityDataStructure[]) {
    // Create an array of promises for each result
    const promises = searchResults.map(async searchResult => {
      if (searchResult) {
        try {
          // Construct the URL for the rating API
          const ratingResponse = await fetch(
            `https://ratings.md/api/v1/widget/profiles/${searchResult.npi}?account=tufts-medicine&id_type=npi`
          );

          // Parse the JSON response into a RatingResponse object
          const ratingData: RatingResponse = await ratingResponse.json();

          // If the ratingData contains a message, it indicates an error, so return early
          if (ratingData.message) {
            return;
          } else {
            searchResult.rating = ratingData.rating;
            searchResult.comment_count = ratingData.comment_count;
          }
        } catch (error) {
          // Error is intentionally unhandled as there's no user-facing action
          // or recovery to be done if this service fails.
        }
      }
    });

    // Wait for all promises to resolve
    await Promise.all(promises);
    // Request an update to the component to reflect the new data
    this.requestUpdate();
  }

  render() {
    this.isMobile = this.resizeController.currentBreakpointRange === 0;

    setTimeout(() => {
      this.createReferenceSplide();
    }, 0);
    const onlyOneResult: boolean = this.doctorResults.length <= 1;
    const wrapperClass = {
      splide: true,
      isMobile: this.isMobile,
      [this.addingclass]: this.addingclass,
    };
    return this.doctorResults.length !== 0
      ? html`
          <div class="heading-with-link">
            <div class="icon-heading-wrapper">
              <tufts-icon
                library="system"
                name="doctor"
                size="4rem"
              ></tufts-icon>
              <tufts-heading level-size="xl" class="block">
                ${this.headingText}
              </tufts-heading>
            </div>
            ${this.viewAllButtonTemplate()}
          </div>
          <div class=${classMap(wrapperClass)} id="splide1">
            ${this.isMobile
              ? html`${this.templateMobile()}`
              : html`${this.templateDesktop(onlyOneResult)}`}
          </div>
        `
      : null;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tufts-carousel-yext-search': TuftsCarouselSearch;
  }
}
